import React, { createContext, useContext, useEffect, useState } from 'react'
import { organizationsAPI } from '../api/organizations'
import type { OrganizationByHost } from '../api/organizations'

interface TenantContextValue {
  /** The org pinned to this hostname, or null on the canonical app domain. */
  tenant: OrganizationByHost | null
  /** True until the by-host probe finishes. */
  isLoading: boolean
}

const TenantContext = createContext<TenantContextValue | undefined>(undefined)

export const useTenant = () => {
  const ctx = useContext(TenantContext)
  if (ctx === undefined) {
    throw new Error('useTenant must be used within a TenantProvider')
  }
  return ctx
}

/**
 * Hosts that always render the canonical multi-org app and should skip the
 * by-host probe entirely. Adding the wildcard catch-all (`*.azurecontainerapps.io`)
 * keeps Azure's auto-generated container app URL in canonical mode.
 */
const CANONICAL_HOST_SUFFIXES = [
  'localhost',
  '127.0.0.1',
  'sondely.com',
  'impactpulse.catalyzeup.ai',
  'impactpulse.catalyzeupdev.com',
  'azurecontainerapps.io',
  'azurestaticapps.net',
]

function isCanonicalHost(hostname: string): boolean {
  const h = hostname.toLowerCase()
  return CANONICAL_HOST_SUFFIXES.some(
    suffix => h === suffix || h.endsWith('.' + suffix),
  )
}

export const TenantProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [tenant, setTenant] = useState<OrganizationByHost | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    const hostname = window.location.hostname

    // Short-circuit on canonical hosts: no point asking the API to confirm
    // there's no tenant for "localhost" or the main app domain.
    if (isCanonicalHost(hostname)) {
      setIsLoading(false)
      return
    }

    let cancelled = false
    organizationsAPI
      .getByHost()
      .then(result => {
        if (!cancelled) setTenant(result)
      })
      .catch(err => {
        // Network error or unexpected status: render canonical UX rather than
        // hard-fail. Logging keeps the failure visible without breaking the page.
        console.error('Tenant probe failed:', err)
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [])

  return (
    <TenantContext.Provider value={{ tenant, isLoading }}>
      {children}
    </TenantContext.Provider>
  )
}
